import { initContract } from '@ts-rest/core';
import { Plain } from './plain';

const c = initContract();

export namespace BookingApi {
    export type Service = {
        id: number;
        name: string;
        description: string;
        priceInCents: number;
        durationInMinutes: number;
        intakeId?: number | null;
    };

    export type Worker = {
        id: number;
        name: string;
        title: string;
        servicesIds: number[];
    };

    export type IntakeQuestion = {
        id: number;
        questionText: string;
        type: 'text' | 'textarea' | 'boolean' | 'enum';
        options: string[] | null;
        required: boolean;
        position: number;
    };

    export type Intake = {
        id: number;
        name: string;
        questions: IntakeQuestion[];
    };

    export type GetBusinessInfoResponse = {
        businessId: number;
        name: string;
        description: string;
        whatsappPhoneNumber: string | null;
        reminders: {
            enabled: boolean;
        };
        booking: {
            maxDaysAhead: number | null;
            maxDurationInMinutes: number | null;
            maxConcurrent: number | null;
            minHoursNotice: number | null;
        };
        services: Service[];
        workers: Worker[];
        payment: {
            percentage: number;
            timeoutMinutes: number | null;
            transferDeadlineHours: number | null;
            method: 'pix' | 'card' | 'boleto' | 'any';
            needsCpf: boolean;
        } | null;
        client: { id: number | null; phoneNumber: string | null; name: string | null } | null;
        intakes?: Intake[];
    };

    export type Booking = {
        id: string;
        date: Plain.SimpleDate;
        time: Plain.TimeOfDay;
        worker: { id: number; name: string } | null;
        services: { id: number; name: string }[];
        isRecurring: boolean;
        isLinkedToSubscription: boolean;
        notes: string;
        confirmation: { confirmed: boolean } | null;
        payment: { paid: true } | { paid: false; invoiceUrl: string } | null;
        intakeStatus?: IntakeStatus;
        pendingIntakeCount?: number;
    };

    export type IntakeAnswer = {
        questionId: number;
        answerText: string;
    };

    /**
     * Booking-scoped view of an intake: the catalog `Intake` (id/name/questions)
     * plus this booking's submission state. Mirrors `IntakeManager.BookingIntake`
     * on the server.
     */
    export type BookingIntake = Intake & {
        status: IntakeStatus;
        answers: IntakeAnswer[];
    };

    export type IntakeSubmission = {
        intakeId: number;
        answers: IntakeAnswer[];
    };

    export type CreateBookingRequest = {
        workerId: number | null;
        servicesIds: number[];
        date: Plain.SimpleDate;
        time: Plain.TimeOfDay;
        notes: string;
        cpfCnpj?: string;
    };

    export type IntakeStatus = 'pending' | 'completed' | null;

    export type GetIntakeResponse = {
        intakes: BookingIntake[];
    };

    export type SubmitIntakeRequest = { intakes: IntakeSubmission[] };

    export type ClientSubscription = {
        id: number;
        priceInCents: number;
        billingDay: number;
        creditCard: { last4: string; brand: string } | null;
        status: 'pending_payment' | 'active' | 'overdue';
        nextBillingDate: string | null;
    };

    export const contract = c.router(
        {
            getBusinessInfo: c.query({
                method: 'GET',
                path: '/:slug',
                pathParams: c.type<{ slug: string }>(),
                responses: {
                    200: c.type<GetBusinessInfoResponse>(),
                },
            }),
            getAvailability: c.query({
                method: 'GET',
                path: '/:slug/availability',
                pathParams: c.type<{ slug: string }>(),
                query: c.type<{ date: string; servicesIds: string; workerId?: string }>(),
                responses: {
                    200: c.type<{ slots: Plain.TimeOfDay[] }>(),
                },
            }),
            setClientName: c.mutation({
                method: 'POST',
                path: '/:slug/client',
                pathParams: c.type<{ slug: string }>(),
                body: c.type<{ name: string }>(),
                responses: {
                    201: c.type<{ id: number; name: string; accessToken: string }>(),
                },
            }),
            getBookings: c.query({
                method: 'GET',
                path: '/:slug/bookings',
                pathParams: c.type<{ slug: string }>(),
                responses: {
                    200: c.type<Booking[]>(),
                },
            }),
            createBooking: c.mutation({
                method: 'POST',
                path: '/:slug/bookings',
                pathParams: c.type<{ slug: string }>(),
                body: c.type<CreateBookingRequest>(),
                responses: {
                    201: c.type<Booking>(),
                },
            }),
            confirmBooking: c.mutation({
                method: 'POST',
                path: '/:slug/bookings/:bookingId/confirm',
                pathParams: c.type<{ slug: string; bookingId: string }>(),
                body: c.noBody(),
                responses: {
                    204: c.noBody(),
                },
            }),
            cancelBooking: c.mutation({
                method: 'DELETE',
                path: '/:slug/bookings/:bookingId',
                pathParams: c.type<{ slug: string; bookingId: string }>(),
                body: c.noBody(),
                responses: {
                    204: c.noBody(),
                },
            }),
            getSubscription: c.query({
                method: 'GET',
                path: '/:slug/subscription',
                pathParams: c.type<{ slug: string }>(),
                responses: {
                    200: c.type<{ subscription: ClientSubscription | null }>(),
                },
            }),
            cancelSubscription: c.mutation({
                method: 'DELETE',
                path: '/:slug/subscription',
                pathParams: c.type<{ slug: string }>(),
                body: c.noBody(),
                responses: {
                    204: c.noBody(),
                },
            }),
            getIntake: c.query({
                method: 'GET',
                path: '/:slug/bookings/:bookingId/intake',
                pathParams: c.type<{ slug: string; bookingId: string }>(),
                responses: {
                    200: c.type<GetIntakeResponse>(),
                },
            }),
            submitIntake: c.mutation({
                method: 'POST',
                path: '/:slug/bookings/:bookingId/intake',
                pathParams: c.type<{ slug: string; bookingId: string }>(),
                body: c.type<SubmitIntakeRequest>(),
                responses: {
                    204: c.noBody(),
                },
            }),
        },
        { strictStatusCodes: true },
    );
}
